import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { trackUsage } from '../../api';
import { PlayerDataProvider, usePlayerData } from './PlayerDataContext';
import PlayerToday from './PlayerToday';
import PlayerForms from './PlayerForms';
import PlayerWellness from './PlayerWellness';
import PlayerMe from './PlayerMe';
import PlayerData from './PlayerData';
import PlayerTeam from './PlayerTeam';
import PlayerSettings from './PlayerSettings';
import PlayerMedical from './PlayerMedical';
import PlayerNutrition from './PlayerNutrition';
import PlayerPhysicalTests from './PlayerPhysicalTests';
import PlayerSsg from './PlayerSsg';
import PlayerTechnical from './PlayerTechnical';
import PersonalDocs from './PersonalDocs';
import PlayerSchedule from './PlayerSchedule';
import FormFillWizard from './FormFillWizard';
import AppointmentPopup from './AppointmentPopup';
import WellnessStaff from '../WellnessStaff';

function NoPlayerRecord({ onLogout }) {
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', gap: 20, padding: 32, textAlign: 'center',
      background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{ fontSize: 48 }}>⚠️</div>
      <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Account not linked</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, maxWidth: 340, margin: 0 }}>
        Your account is not linked to a player profile. Please contact your administrator.
      </p>
      <button onClick={() => { onLogout(); navigate('/login', { replace: true }); }}
        style={{ padding: '12px 28px', borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', minHeight: 44 }}>
        Sign out
      </button>
    </div>
  );
}

// ── Header — needs context for badges ────────────────────────────────────────
function PlayerHeader({ user, isMobile, onLogout }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { todayWellness, todayRpePitch, todayRpeGym, rpeForms, pendingForms } = usePlayerData();

  // Stays lit until BOTH applicable RPE forms are in (2026-08-17) — the old single todayRpe
  // check cleared the badge as soon as either one was submitted.
  const formsBadge = !todayWellness || !todayRpePitch || (rpeForms?.gym && !todayRpeGym) || pendingForms.length > 0;

  const NAV = [
    { label: 'Today', path: '/player',       exact: true },
    { label: 'Forms', path: '/player/forms', badge: formsBadge },
    { label: 'Me',    path: '/player/me'  },
  ];

  const isActive = item =>
    item.exact ? pathname === item.path : pathname === item.path || pathname.startsWith(item.path + '/');

  const onSettings = pathname === '/player/settings' || pathname.startsWith('/player/settings/');

  return (
    <header style={{ borderBottom: '1px solid var(--border-strong)', background: 'var(--bg-app)', flexShrink: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: isMobile ? '10px 10px' : '12px 20px' }}>
        {/* Logo */}
        <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt=""
          style={{ height: isMobile ? 24 : 32, width: 'auto', flexShrink: 0 }} />

        {!isMobile && (
          <div style={{ fontWeight: 900, fontSize: 18, fontStyle: 'italic', fontFamily: "'Arial Black', Impact, sans-serif", letterSpacing: '-0.02em', flexShrink: 0, marginRight: 4 }}>
            HeraLab<span style={{ color: '#2563eb' }}>X</span>
          </div>
        )}

        {/* Nav pills */}
        <div style={{ display: 'flex', gap: 3, marginLeft: isMobile ? 2 : 8 }}>
          {NAV.map(item => {
            const active = isActive(item);
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                style={{
                  position: 'relative',
                  padding: isMobile ? '5px 9px' : '5px 14px',
                  borderRadius: 99,
                  border: active ? '1px solid #3b82f6' : '1px solid var(--border)',
                  background: active ? '#3b82f6' : 'transparent',
                  color: active ? '#fff' : 'var(--text-muted)',
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', minHeight: 30,
                }}>
                {item.label}
                {item.badge && (
                  <span style={{ position: 'absolute', top: 3, right: 3, width: 6, height: 6, borderRadius: '50%', background: '#ef4444' }} />
                )}
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1 }} />

        {!isMobile && (
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 4 }}>
            {user?.name || user?.username}
          </span>
        )}

        {/* Gear */}
        <button onClick={() => navigate(onSettings ? '/player' : '/player/settings')}
          title="Settings"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: `1px solid ${onSettings ? '#3b82f6' : 'var(--border-strong)'}`, background: onSettings ? 'rgba(59,130,246,0.12)' : 'var(--bg-card)', color: onSettings ? '#60a5fa' : 'var(--text-secondary)', cursor: 'pointer', padding: 0, flexShrink: 0 }}>
          <Settings size={15} strokeWidth={1.8} />
        </button>

        {/* Sign out */}
        <button onClick={() => { onLogout(); navigate('/login', { replace: true }); }}
          style={{ padding: isMobile ? '5px 9px' : '7px 12px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          Sign out
        </button>
      </div>
    </header>
  );
}

// EWMA is intentionally excluded — players should not see their own ACWR/load-risk data.
const ALL_PLAYER_GPS_IDS = [
  'team-training', 'player-chronic', 'max-speed',
  'individual-targets', 'match-performance', 'match-chronic', 'in-match-analysis',
  'rehab-training',
];

// ── Inner shell (needs context) ───────────────────────────────────────────────
function PlayerShell({ user, isMobile, onLogout }) {
  const { playerRecord } = usePlayerData();
  const { pathname } = useLocation();

  // 2026-08-24: same page-view logging as AppShell's (App.js), added separately here since the
  // player app is a fully separate component tree with its own react-router <Routes> instead of
  // AppShell's activePage string state — there's no shared choke point to hook into. Player
  // routes are logged as 'player/<path after /player>' (e.g. 'player/me/gps/player-chronic') so
  // Usage Analytics can tell staff-app and player-app activity apart.
  useEffect(() => {
    const suffix = pathname.replace(/^\/player\/?/, '');
    trackUsage(user.id, user.username, user.name, `player/${suffix || 'today'}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Goalkeepers get a single-purpose GPS & Load tab (2026-08-20) — the team-training/
  // chronic/match-performance etc. pages are all built around outfield metrics that don't
  // apply to a GK's own game, so tapping GPS & Load takes a GK straight to (and only to)
  // their own Goalkeeper report, bypassing allowedGpsTabs entirely rather than layering
  // another admin setting on top of a rule that's really about the player's position.
  const allowedGpsIds = playerRecord?.position === 'GK'
    ? ['goalkeeper']
    : (user.allowedGpsTabs == null ? ALL_PLAYER_GPS_IDS : ALL_PLAYER_GPS_IDS.filter(id => user.allowedGpsTabs.includes(id)));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', background: 'var(--bg-app)', color: 'var(--text-primary)', fontFamily: 'Inter, sans-serif' }}>
      <PlayerHeader user={user} isMobile={isMobile} onLogout={onLogout} />
      <AppointmentPopup />
      <main style={{ flex: 1, overflow: 'auto' }}>
        <Routes>
          {/* Today */}
          <Route index element={<PlayerToday isMobile={isMobile} user={user} />} />

          {/* Forms */}
          <Route path="forms" element={<PlayerForms isMobile={isMobile} />} />
          <Route path="forms/wellness" element={<PlayerWellness playerRecord={playerRecord} defaultTab="wellness" isMobile={isMobile} />} />
          {/* forms/rpe kept as the pitch route (2026-08-17) so any bookmark/push-notification
              deep link from before the pitch/gym split still lands on the pitch form. */}
          <Route path="forms/rpe"      element={<PlayerWellness playerRecord={playerRecord} defaultTab="rpe" sessionType="pitch" isMobile={isMobile} />} />
          <Route path="forms/rpe-gym"  element={<PlayerWellness playerRecord={playerRecord} defaultTab="rpe" sessionType="gym"   isMobile={isMobile} />} />
          <Route path="forms/nutrition" element={<PlayerNutrition playerRecord={playerRecord} />} />
          <Route path="forms/:formId" element={<FormFillWizard playerRecord={playerRecord} />} />

          {/* Me hub */}
          <Route path="me" element={<PlayerMe />} />
          <Route path="me/gps"
            element={<PlayerData playerRecord={playerRecord} isMobile={isMobile} tabIds={allowedGpsIds} backPath="/player/me" user={user} />} />
          <Route path="me/gps/:tab"
            element={<PlayerData playerRecord={playerRecord} isMobile={isMobile} tabIds={allowedGpsIds} backPath="/player/me" user={user} />} />
          <Route path="me/wellness"
            element={<WellnessStaff playerOverride={playerRecord} forceIndividual />} />
          <Route path="me/medical"
            element={<PlayerMedical playerRecord={playerRecord} />} />
          <Route path="me/tests"
            element={<PlayerPhysicalTests playerRecord={playerRecord} />} />
          <Route path="me/ssg"
            element={<PlayerSsg playerRecord={playerRecord} />} />
          <Route path="me/technical"
            element={<PlayerTechnical playerRecord={playerRecord} />} />
          <Route path="me/schedule" element={<PlayerSchedule />} />
          <Route path="me/docs"
            element={<PersonalDocs playerRecord={playerRecord} />} />

          {/* Team — kept for legacy navigation (e.g. Today → last session tap) */}
          <Route path="team" element={<PlayerTeam user={user} isMobile={isMobile} />} />

          {/* Settings */}
          <Route path="settings" element={<PlayerSettings user={user} />} />

          {/* Legacy redirect */}
          <Route path="data"       element={<Navigate to="/player/me/gps" replace />} />
          <Route path="data/:tab"  element={<Navigate to="/player/me/gps" replace />} />

          <Route path="*" element={<Navigate to="/player" replace />} />
        </Routes>
      </main>
    </div>
  );
}

// ── Root export — wraps with Provider ────────────────────────────────────────
export default function PlayerApp({ user, isMobile, onLogout }) {
  if (user.playerRecord === null) {
    return <NoPlayerRecord onLogout={onLogout} />;
  }

  return (
    <PlayerDataProvider playerRecord={user.playerRecord} sessionToken={user.sessionToken}>
      <PlayerShell user={user} isMobile={isMobile} onLogout={onLogout} />
    </PlayerDataProvider>
  );
}
